/**
 * The template's dashboard filters, editable. The workspace filter never
 * reaches this component — the server hides it (spec §4).
 *
 * Styled like the Dashboard's control band: small uppercase labels, the same
 * white bordered fields as PeriodSelector, and the black/yellow action button.
 *
 * @module components/looker/LookerFilterBar
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelFilter } from '@/components/ChannelFilter';
import type { TFunction } from 'i18next';
import type { LookerFilter } from '@/services/looker/lookerTypes';
import { addDays, bangkokToday, customToken, isValidCustomRange } from '@/services/looker/lookerDateRange';

/** Label above a control — the band-stat label on the Dashboard. */
export const LOOKER_LABEL_CLASS = 'flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-700';

/** A field in the band — the same treatment as PeriodSelector. */
export const LOOKER_FIELD_CLASS =
  'h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm normal-case tracking-normal font-normal text-ink-900 ' +
  'focus:border-ink-950 focus:outline-none focus:ring-2 focus:ring-ink-950 disabled:cursor-not-allowed disabled:opacity-50';

/** The Dashboard's hero action (HERO_ACTION_CLASS in Home): black with yellow text. */
export const LOOKER_ACTION_CLASS =
  'inline-flex h-9 items-center gap-1 rounded-lg px-3 text-sm font-bold uppercase tracking-wide ' +
  'bg-ink-950 text-primary-500 hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-50';

interface LookerFilterBarProps {
  filters: LookerFilter[];
  onApply: (values: Record<string, string>) => void;
  disabled?: boolean;
}

/**
 * The D1 presets, sent to the server as tokens it resolves to exact Bangkok
 * dates. '' = no date filter = All time. 'custom' opens two date inputs.
 */
export const DATE_OPTIONS: ReadonlyArray<{ value: string; label: (t: TFunction) => string }> = [
  { value: '7d', label: (t) => t('looker.dates.d7') },
  { value: '14d', label: (t) => t('looker.dates.d14') },
  { value: '28d', label: (t) => t('looker.dates.d28') },
  { value: '30d', label: (t) => t('looker.dates.d30') },
  { value: 'mtd', label: (t) => t('looker.dates.mtd') },
  { value: 'last_month', label: (t) => t('looker.dates.lastMonth') },
  { value: 'custom', label: (t) => t('looker.dates.custom') },
  { value: '', label: (t) => t('looker.dates.all') },
];

const CUSTOM_PREFIX = 'custom:';
/** 'custom:A:B' to [A, B]; anything else to two empty strings. */
const customRangeOf = (value: string): [string, string] => {
  if (!value.startsWith(CUSTOM_PREFIX)) return ['', ''];
  const [from = '', to = ''] = value.slice(CUSTOM_PREFIX.length).split(':');
  return [from, to];
};

const valuesOf = (filters: LookerFilter[]): Record<string, string> =>
  Object.fromEntries(filters.map((f) => [f.name, f.value]));

/**
 * Date and channel filters look and behave like the app's own: the period
 * select and the remembered channel dropdown, both applied on pick. The page
 * sends the channel selection itself (it is shared app state), so the channel
 * control here only draws it. Anything else stays a free-text Looker
 * expression behind an Apply button.
 */
export const LookerFilterBar: React.FC<LookerFilterBarProps> = ({ filters, onApply, disabled = false }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => valuesOf(filters));
  // Picking "Custom…" opens the two dates without applying anything yet.
  const [customOpen, setCustomOpen] = useState(false);
  useEffect(() => {
    setDraft(valuesOf(filters));
    setCustomOpen(false);
  }, [filters]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const appliedCustom = filters.map((f) => f.value).find((v) => v.startsWith(CUSTOM_PREFIX)) ?? '';
  useEffect(() => {
    const [from, to] = customRangeOf(appliedCustom);
    setCustomFrom(from);
    setCustomTo(to);
  }, [appliedCustom]);
  if (filters.length === 0) return null;

  const hasText = filters.some((f) => f.kind === 'text');
  const pick = (name: string, value: string) => {
    const next = { ...draft, [name]: value };
    setDraft(next);
    onApply(next);
  };

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onApply(draft);
      }}
    >
      {filters.map((f) => {
        if (f.kind === 'channel') {
          return (
            // No label above it: the app's dropdown already reads "Channels: …".
            <ChannelFilter key={f.name} disabled={disabled} />
          );
        }
        if (f.kind === 'objective') {
          const objective = draft[f.name] ?? '';
          const knownObjective = ['', 'awareness', 'consideration', 'conversion'].includes(objective);
          return (
            <label key={f.name} className={LOOKER_LABEL_CLASS}>
              {f.title}
              <select aria-label={f.title} className={LOOKER_FIELD_CLASS} value={draft[f.name] ?? ''} onChange={(e) => pick(f.name, e.target.value)} disabled={disabled}>
                <option value="">{t('looker.objectives.all')}</option>
                <option value="awareness">{t('looker.objectives.awareness')}</option>
                <option value="consideration">{t('looker.objectives.consideration')}</option>
                <option value="conversion">{t('looker.objectives.conversion')}</option>
                {/* A Looker-made default ("awareness,conversion") stays selectable, so the select shows what is applied. */}
                {!knownObjective && <option value={objective}>{objective}</option>}
              </select>
            </label>
          );
        }
        if (f.kind === 'date') {
          const value = draft[f.name] ?? '';
          const isCustom = value.startsWith(CUSTOM_PREFIX);
          const selected = isCustom || customOpen ? 'custom' : value;
          const known = DATE_OPTIONS.some((p) => p.value === selected);
          const yesterday = addDays(bangkokToday(), -1);
          const showCustom = customOpen || isCustom;
          // A typed date can ignore max=, so the server's own rule decides.
          const canApply = isValidCustomRange(customFrom, customTo, bangkokToday());
          return (
            <React.Fragment key={f.name}>
              <label className={LOOKER_LABEL_CLASS}>
                {f.title}
                <select
                  aria-label={f.title}
                  className={LOOKER_FIELD_CLASS}
                  value={selected}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setCustomOpen(true);
                      return;
                    }
                    setCustomOpen(false);
                    pick(f.name, e.target.value);
                  }}
                  disabled={disabled}
                >
                  {DATE_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label(t)}</option>)}
                  {/* A Looker-made template's free-text date ("this year") stays
                      selectable, so the select shows what is actually applied. */}
                  {!known && <option value={value}>{value}</option>}
                </select>
              </label>
              {showCustom && (
                <>
                  <label className={LOOKER_LABEL_CLASS}>
                    {t('looker.dateFrom')}
                    <input type="date" aria-label={t('looker.dateFrom')} className={LOOKER_FIELD_CLASS} max={yesterday}
                      value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} disabled={disabled} />
                  </label>
                  <label className={LOOKER_LABEL_CLASS}>
                    {t('looker.dateTo')}
                    <input type="date" aria-label={t('looker.dateTo')} className={LOOKER_FIELD_CLASS} max={yesterday}
                      value={customTo} onChange={(e) => setCustomTo(e.target.value)} disabled={disabled} />
                  </label>
                  <button type="button" className={LOOKER_ACTION_CLASS} disabled={disabled || !canApply}
                    onClick={() => pick(f.name, customToken(customFrom, customTo))}>
                    {t('looker.applyDates')}
                  </button>
                </>
              )}
            </React.Fragment>
          );
        }
        return (
          <label key={f.name} className={LOOKER_LABEL_CLASS}>
            {f.title}
            <input
              aria-label={f.title}
              className={`${LOOKER_FIELD_CLASS} w-40`}
              placeholder={t('looker.filterAny')}
              value={draft[f.name] ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, [f.name]: e.target.value }))}
              disabled={disabled}
            />
          </label>
        );
      })}
      {hasText && (
        <button type="submit" disabled={disabled} className={LOOKER_ACTION_CLASS}>
          {t('looker.applyFilters')}
        </button>
      )}
    </form>
  );
};
